import { PublicClientApplication, BrowserCacheLocation } from '@azure/msal-browser';

const tenantId = (import.meta.env.VITE_AZURE_AD_TENANT_ID || '').trim();
const clientId = (import.meta.env.VITE_AZURE_AD_SPA_CLIENT_ID || '').trim();

/** @returns {string} */
export function getApiScope() {
  return (import.meta.env.VITE_AZURE_AD_API_SCOPE || '').trim();
}

/** Tenant + SPA client id (MSAL app can initialize). */
export function hasMsalSpaRegistration() {
  return Boolean(tenantId && clientId);
}

/** Full frontend config for a real Microsoft login (incl. API scope). */
export function isMsalConfigured() {
  return hasMsalSpaRegistration() && Boolean(getApiScope());
}

/**
 * Show "Sign in with Microsoft" when fully configured, or in dev if VITE_MSAL_SHOW_LOGIN_BUTTON is set.
 * @returns {boolean}
 */
export function isMicrosoftLoginButtonVisible() {
  if (isMsalConfigured()) {
    return true;
  }
  const force =
    import.meta.env.DEV &&
    /^(1|true|yes)$/i.test(String(import.meta.env.VITE_MSAL_SHOW_LOGIN_BUTTON || '').trim());
  return Boolean(force);
}

const AUTH_PROVIDER_KEY = 'sueAuthProvider';

/** @param {'legacy' | 'entra'} provider */
export function setStoredAuthProvider(provider) {
  localStorage.setItem(AUTH_PROVIDER_KEY, provider);
}

/** @returns {'legacy' | 'entra' | null} */
export function getStoredAuthProvider() {
  const v = localStorage.getItem(AUTH_PROVIDER_KEY);
  return v === 'entra' || v === 'legacy' ? v : null;
}

export function clearStoredAuthProvider() {
  localStorage.removeItem(AUTH_PROVIDER_KEY);
}

let instance = null;

/**
 * @returns {Promise<import('@azure/msal-browser').PublicClientApplication | null>}
 */
export async function ensureMsalReady() {
  if (!hasMsalSpaRegistration()) {
    return null;
  }
  if (!instance) {
    instance = new PublicClientApplication({
      auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: typeof window !== 'undefined' ? window.location.origin : '',
      },
      cache: {
        cacheLocation: BrowserCacheLocation.SessionStorage,
        storeAuthStateInCookie: false,
      },
    });
    await instance.initialize();
  }
  return instance;
}

/**
 * Resolve the redirect response after loginRedirect (must run early on startup).
 * @returns {Promise<import('@azure/msal-browser').AuthenticationResult | null>}
 */
export async function handleLoginRedirectReturn() {
  const msal = await ensureMsalReady();
  if (!msal) {
    return null;
  }
  return msal.handleRedirectPromise();
}

/**
 * Starts interactive Microsoft login (full-page redirect).
 */
export async function loginRedirectToMicrosoft() {
  const msal = await ensureMsalReady();
  const scope = getApiScope();
  if (!msal || !scope) {
    throw new Error(
      'Microsoft login is not configured. Set VITE_AZURE_AD_TENANT_ID, VITE_AZURE_AD_SPA_CLIENT_ID, and VITE_AZURE_AD_API_SCOPE.',
    );
  }
  setStoredAuthProvider('entra');
  await msal.loginRedirect({ scopes: [scope] });
}

/**
 * @returns {Promise<string | null>}
 */
export async function acquireEntraAccessToken() {
  const msal = await ensureMsalReady();
  const scope = getApiScope();
  if (!msal || !scope) {
    return null;
  }
  const accounts = msal.getAllAccounts();
  if (!accounts.length) {
    return null;
  }
  try {
    const result = await msal.acquireTokenSilent({
      account: accounts[0],
      scopes: [scope],
    });
    return result.accessToken;
  } catch {
    return null;
  }
}

export async function logoutMicrosoftRedirect() {
  const msal = await ensureMsalReady();
  if (msal) {
    const accounts = msal.getAllAccounts();
    await msal.logoutRedirect({
      account: accounts[0],
      postLogoutRedirectUri: typeof window !== 'undefined' ? window.location.origin : '',
    });
  }
}

/** Clear MSAL browser cache without org-wide sign-out (401 / inactivity / return to login). */
export async function clearMsalLocalSession() {
  const msal = await ensureMsalReady();
  if (msal) {
    await msal.clearCache();
  }
}
